import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { ShieldAlert } from 'lucide-react';
import { useShop } from '../lib/shop-context.js';
import { Button } from '../components/ui/button.js';
import { Input, Select, Textarea } from '../components/ui/field.js';

/**
 * Trust & safety on the customer's side (CLAUDE.md §6.7).
 *
 * Two pieces: the notice a suspended shop shows instead of a catalogue, and
 * the report form. Both are deliberately plain — this is not a moment to be
 * clever with a shop's own theme tokens.
 */

/**
 * What a customer sees when a shop has been suspended.
 *
 * The link is not 404'd: §6.8 promises that a slug pasted across Facebook
 * keeps working forever, and a customer who already has an unpaid order needs
 * their order page to load. So the shop resolves, says plainly that it is not
 * selling, and points at the one thing still worth doing.
 */
export function SuspendedNotice() {
  const { data, t } = useShop();
  const shop = data.shop;

  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <ShieldAlert className="size-8 opacity-70" aria-hidden />
      <h2 className="text-lg font-semibold">{t('trust.shopSuspended')}</h2>
      <p className="max-w-sm text-sm opacity-70">{t('trust.shopSuspendedHint')}</p>

      {shop.suspendedReason ? (
        <p
          className="mt-2 max-w-sm rounded-[var(--radius-card)] px-4 py-3 text-sm"
          style={{ background: 'var(--color-surface)' }}
        >
          {shop.suspendedReason}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The report mechanism (§6.7).
 *
 * A details/summary rather than a route: reporting is rare, and a customer who
 * wants it is already at the bottom of the page looking for a way to complain.
 * No account, no email field required — the moment this asks somebody to sign
 * up, it stops receiving reports.
 */
export function ReportShop({ productId = null }: { productId?: string | null }) {
  const { data, locale, t } = useShop();
  const shop = data.shop;

  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [contact, setContact] = useState('');

  const options = useQuery({
    queryKey: ['report-reasons'],
    queryFn: () => api.reportOptions(),
    staleTime: 60 * 60_000,
  });

  const submit = useMutation({
    mutationFn: () =>
      api.report({
        shopSlug: shop.slug,
        productId,
        reason,
        detail: detail.trim(),
        ...(contact.trim() ? { contact: contact.trim() } : {}),
      }),
  });

  if (submit.isSuccess) {
    return (
      <p className="mt-6 text-center text-xs opacity-60">{t('trust.reportThanks')}</p>
    );
  }

  return (
    <details className="mt-6">
      <summary className="cursor-pointer text-center text-xs opacity-55">
        {t('trust.reportShop')}
      </summary>

      <div className="mt-3 flex flex-col gap-2">
        <p className="text-xs opacity-60">{t('trust.reportHint')}</p>

        <Select
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-label={t('trust.reportReason')}
        >
          <option value="">{t('trust.reportReason')}</option>
          {options.data?.reportReasons.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label[locale] || entry.label.en}
            </option>
          ))}
        </Select>

        <Textarea
          rows={3}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          placeholder={t('trust.reportDetail')}
        />

        <Input
          value={contact}
          onChange={(event) => setContact(event.target.value)}
          placeholder={t('trust.reportContact')}
        />

        <Button variant="ghost" width="auto"
          disabled={!reason || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? t('common.loading') : t('trust.reportSubmit')}
        </Button>

        {submit.isError ? (
          <p className="text-xs" style={{ color: 'var(--color-accent)' }}>
            {t('errors.internal')}
          </p>
        ) : null}
      </div>
    </details>
  );
}
